import React, { useState } from 'react';
import { gameState } from '../game/state/GameState';
import Leaderboard from '../game/Leaderboard';
import { claimTile, solveTile, completeBootcampProgress } from '../game/actions/TileActions';
import { TILE_STATUS } from '../game/constants';
import { rankSlicesForSuggestion } from '../game/voxel/slicePipeline';
import { getDailyModelId, getModelById } from '../game/voxel/models';
import { resetGuessState, resetEnergyState } from '../game/meta/progression';
import { resetAssistState } from '../game/meta/atelierAssists';
import { getApiBase, IS_LOCAL_MODE, canUseDebugTools } from '../config/game_config';
import { BOOTCAMP_STORAGE_KEY } from '../game/config/bootcampConfig';
import { getAnalyticsSnapshot, getShareClickCount } from '../game/meta/analytics';
import DebugModelBrowser from './DebugModelBrowser';

const GameControls = ({ onFeedback }) => {
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [modelBrowser, setModelBrowser] = useState(null); // null | 'master' | 'library'

    const handleReseed = async () => {
        if (!confirm('Wipe today\'s mystery and reseed? Continue?')) return;
        const adminKey = import.meta.env.VITE_ADMIN_KEY || '';
        if (!IS_LOCAL_MODE && !adminKey) {
            alert('Set VITE_ADMIN_KEY in .env to match Worker ADMIN_KEY secret.');
            return;
        }
        try {
            // Fresh day feel-test: clear spent guesses / energy / slice drafts
            resetGuessState();
            resetEnergyState();
            resetAssistState();
            try {
                Object.keys(localStorage)
                    .filter((k) => k.startsWith('vq_draft_') || k.startsWith('vq_contrib_'))
                    .forEach((k) => localStorage.removeItem(k));
            } catch {
                /* ignore */
            }
            const base = getApiBase();
            const headers = {
                'X-Syndicate-Id': gameState.currentSquadId || 'public-syndicate-0000',
            };
            if (adminKey) headers['X-Admin-Key'] = adminKey;
            await fetch(`${base}/admin/seed`, {
                method: 'POST',
                headers,
            }).then(async (res) => {
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || res.statusText);
                }
                return res.json();
            });
            // Drop forceReveal / forceBootcamp so post-reveal debug state can't latch
            const url = new URL(window.location.href);
            url.searchParams.delete('forceReveal');
            url.searchParams.delete('forceBootcamp');
            if (!url.searchParams.has('skipBootcamp')) {
                url.searchParams.set('skipBootcamp', '1');
            }
            window.location.href = url.toString();
        } catch (e) {
            console.error(e);
            alert(`Failed to reseed mystery.\n${e?.message || e}`);
        }
    };

    const handleResetGuesses = () => {
        resetGuessState();
        resetEnergyState();
        resetAssistState();
        onFeedback?.('Guess attempts + energy + assists restored for today');
        window.location.reload();
    };

    // Helper to Auto-Solve All Tiles (Debug)
    const handleAutoSolve = async () => {
        if (!confirm('🪄 MAGIC: Auto-Solving all remaining tiles...')) return;

        const allTiles = Array.from(gameState.vault.getTiles());
        const openTiles = allTiles.filter((t) => t.status === TILE_STATUS.OPEN);
        const claimedMine = allTiles.filter(
            (t) =>
                t.status === TILE_STATUS.CLAIMED &&
                t.claimedBy === gameState.currentUser?.id &&
                t.data?.solutionGrid
        );

        const queue = [
            ...claimedMine,
            ...openTiles.filter((t) => t.data?.solutionGrid),
        ];

        console.log(`[AutoSolve] Found ${queue.length} solvable tiles.`);

        if (queue.length === 0) {
            alert(
                'No open tiles left (vault already complete).\nUse RESEED DAILY VAULT first.'
            );
            return;
        }

        for (const tile of queue) {
            console.log(`[AutoSolve] Processing Tile ${tile.id}...`);
            if (tile.status === TILE_STATUS.OPEN) {
                await claimTile(tile.id);
            }
            if (tile.data?.solutionGrid) {
                await solveTile(tile.id, tile.data.solutionGrid);
            }
            await new Promise((r) => setTimeout(r, 200));
        }

        window.location.reload();
    };

    /** Solve a single open slice — prefers safest (least spoiler) remaining plane. */
    const handleSolveOneSlice = async () => {
        const allTiles = Array.from(gameState.vault.getTiles());
        const me = gameState.currentUser?.id;
        const date = new Date().toISOString().split('T')[0];
        const model = getModelById(getDailyModelId(date, gameState.mysteryTier || 'atelier'));
        const size = model?.size || 16;

        const mine = allTiles.find(
            (t) => t.status === TILE_STATUS.CLAIMED && t.claimedBy === me && t.data?.solutionGrid
        );
        const open = allTiles.filter(
            (t) => t.status === TILE_STATUS.OPEN && t.data?.solutionGrid
        );
        const avoid = allTiles.filter(
            (t) =>
                t.status === TILE_STATUS.SOLVED ||
                (t.status === TILE_STATUS.CLAIMED && t.claimedBy === me)
        );
        const ranked = rankSlicesForSuggestion(open, size, { avoid });
        const tile = mine || ranked[0];

        if (!tile) {
            onFeedback?.('No solvable open/claimed slice left');
            return;
        }

        const label =
            tile.data?.axis != null
                ? `${String(tile.data.axis).toUpperCase()}=${tile.data.index}`
                : tile.id;

        if (tile.status === TILE_STATUS.OPEN) {
            const claimed = await claimTile(tile.id);
            if (!claimed.success) {
                onFeedback?.(claimed.error || 'CLAIM FAILED');
                return;
            }
        }

        const solved = await solveTile(tile.id, tile.data.solutionGrid);
        if (!solved.success) {
            onFeedback?.(solved.error || 'SOLVE FAILED');
            return;
        }

        onFeedback?.(`DEBUG: solved ${label} (safer-order) — watch reconstruction`);
    };

    if (!canUseDebugTools()) return null;

    return (
        <div style={{ border: '1px dashed red', padding: '10px', background: '#000', color: '#fff', marginBottom: '10px' }}>
            <h4 style={{ marginTop: 0 }}>Debug Controls (Dev Only)</h4>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {!gameState.isBootcampMode && (
                        <>
                            <button onClick={handleReseed} style={{ cursor: 'pointer', background: '#440000', color: '#f00', border: '1px solid #f00', padding: '5px' }}>
                                ⚠️ RESEED DAILY VAULT
                            </button>
                            <button onClick={handleAutoSolve} style={{ cursor: 'pointer', background: '#330044', color: '#d0f', border: '1px solid #d0f', padding: '5px' }}>
                                🪄 AUTO SOLVE VAULT
                            </button>
                            <button onClick={handleSolveOneSlice} style={{ cursor: 'pointer', background: '#002244', color: '#4af', border: '1px solid #4af', padding: '5px' }}>
                                ◧ SOLVE ONE SLICE
                            </button>
                            <button onClick={handleResetGuesses} style={{ cursor: 'pointer', background: '#003322', color: '#3f9', border: '1px solid #3f9', padding: '5px' }}>
                                ↺ RESET GUESSES
                            </button>
                        </>
                    )}
                    <button onClick={() => window.location.reload()} style={{ cursor: 'pointer', background: '#333', color: '#fff', padding: '5px' }}>
                        ↻ RELOAD APP
                    </button>
                    <button
                        onClick={() => {
                            const snap = getAnalyticsSnapshot();
                            console.log('[VQ analytics]', snap);
                            onFeedback?.(
                                `Shares: ${getShareClickCount()} · events in console (${snap.events.length})`
                            );
                        }}
                        style={{ cursor: 'pointer', background: '#222244', color: '#aaf', border: '1px solid #aaf', padding: '5px' }}
                    >
                        📊 FUNNEL COUNTS
                    </button>
                    <button onClick={() => {
                        if (confirm('Reset to BOOTCAMP? This will clear local storage.')) {
                            localStorage.clear();
                            window.location.reload();
                        }
                    }} style={{ cursor: 'pointer', background: '#444400', color: '#ff0', border: '1px solid #ff0', padding: '5px' }}>
                        👶 RESET TO BOOTCAMP
                    </button>
                    {gameState.isBootcampMode && (
                        <button onClick={() => {
                            if (confirm('Skip Bootcamp and go to Daily Vault?')) {
                                localStorage.setItem(BOOTCAMP_STORAGE_KEY, 'true');
                                completeBootcampProgress().finally(() => window.location.reload());
                            }
                        }} style={{ cursor: 'pointer', background: '#004444', color: '#0ff', border: '1px solid #0ff', padding: '5px' }}>
                            🎓 SKIP BOOTCAMP
                        </button>
                    )}
                    <button
                        onClick={() => setModelBrowser('master')}
                        style={{ cursor: 'pointer', background: '#004400', color: '#0f0', border: '1px solid #0f0', padding: '5px' }}
                    >
                        👁 SHOW MASTER MODEL
                    </button>
                    <button
                        onClick={() => setModelBrowser('library')}
                        style={{ cursor: 'pointer', background: '#003355', color: '#6cf', border: '1px solid #6cf', padding: '5px' }}
                    >
                        📚 VALIDATE MODEL LIBRARY
                    </button>
                    <button onClick={() => setShowLeaderboard(true)} style={{ cursor: 'pointer', background: '#442200', color: '#fa0', border: '1px solid #fa0', padding: '5px' }}>
                        🏆 LEADERBOARD
                    </button>
            </div>

            {showLeaderboard && <Leaderboard onClose={() => setShowLeaderboard(false)} />}
            {modelBrowser && (
                <DebugModelBrowser mode={modelBrowser} onClose={() => setModelBrowser(null)} />
            )}
        </div>
    );
};

export default GameControls;
